import { useState, useMemo } from "react";
import { useGetNetworkTrendsQuery } from "../../../../../../../store/allNetworkStats";
import { NetworkTrendsPeriod } from "../../../../../../../store/allNetworkStats/types";

export interface INetworkGraphData {
  name: string;
  shareholders?: number | null;
  shares?: number | null;
}

export const useNetworkGraphData = () => {
  const [timeframe, setTimeframe] = useState<NetworkTrendsPeriod>("3m");

  const { data: trendsData, isFetching } = useGetNetworkTrendsQuery(timeframe);

  const { graphData, shareholderMax, sharesMax } = useMemo(() => {
    if (!trendsData || trendsData.length === 0 || isFetching) {
      return {
        graphData: [] as INetworkGraphData[],
        shareholderMax: 0,
        sharesMax: 0,
      };
    }

    // Convert API data to graph format, ensuring nulls are handled
    const processedData = trendsData.map((item) => {
      const mappedItem = {
        name: item.recordDate,
        shareholders: item.totalCustomers,
        shares: item.totalShares,
      };
      return mappedItem;
    });

    // Calculate max values for scaling, ignoring nulls
    const validShareholderValues = processedData
      .map((d) => d.shareholders)
      .filter((val): val is number => val !== null);
    const validSharesValues = processedData
      .map((d) => d.shares)
      .filter((val): val is number => val !== null);

    const shareholderMax =
      validShareholderValues.length > 0
        ? Math.max(...validShareholderValues) * 1.1
        : 0;
    const sharesMax =
      validSharesValues.length > 0 ? Math.max(...validSharesValues) * 1.1 : 0;

    return {
      graphData: processedData,
      shareholderMax,
      sharesMax,
    };
  }, [trendsData, isFetching]);

  return {
    graphData,
    shareholderMax,
    sharesMax,
    timeframe,
    setTimeframe,
    isLoading: isFetching,
  };
};
